/**
 * Search Scope Detector
 *
 * Auto-detects the appropriate search scope from a query string.
 * This provides automatic CLI recommendations while allowing manual override in GUI.
 *
 * Detection logic:
 * - Query with branches: "Glc-b4-Man[GlcA-a3]-b4-Glc" → 'anywhere'
 * - Query with only main chain: "Glc-b4-Man-b4-Glc" → 'mainChain'
 * - Query with only branches: "[GlcA-a3]" → 'branches'
 */

/**
 * Extract branch content and main chain from query
 * Returns { mainChainString, hasBranches }
 *
 * @param {string} queryString - Query string to analyze
 * @returns {object} - { mainChainString: string, hasBranches: boolean }
 */
function extractBranchInfo(queryString) {
  let mainChainString = '';
  let hasBranches = false;
  let i = 0;

  while (i < queryString.length) {
    const char = queryString[i];

    if (char === '[') {
      // Start of branch - find matching closing bracket
      hasBranches = true;
      let depth = 1;
      let j = i + 1;
      while (j < queryString.length && depth > 0) {
        if (queryString[j] === '[') depth++;
        if (queryString[j] === ']') depth--;
        j++;
      }
      i = j; // Skip past the branch
    } else {
      // Regular character - add to main chain
      mainChainString += char;
      i++;
    }
  }

  return {
    mainChainString: mainChainString.trim(),
    hasBranches: hasBranches
  };
}

/**
 * Detect search scope from query syntax
 * Returns recommended search scope based on query structure.
 *
 * @param {string} queryString - Query string to analyze
 * @returns {string} - Recommended scope: 'anywhere' | 'mainChain' | 'branches'
 */
export function detectSearchScopeFromQuery(queryString) {
  if (!queryString || typeof queryString !== 'string' || queryString.trim() === '') {
    return 'anywhere'; // Default to anywhere for empty queries
  }

  const { mainChainString, hasBranches } = extractBranchInfo(queryString);

  // No branches → main chain only
  if (!hasBranches) {
    return 'mainChain';
  }

  // Has brackets - check if there's a main chain pattern
  const mainChainContent = mainChainString.replace(/-/g, '').replace(/\s+/g, '').trim();

  if (mainChainContent === '') {
    // Only branches, no main chain → branches only
    return 'branches';
  }

  // Has both main chain and branches → anywhere
  return 'anywhere';
}

/**
 * Check if a query is a branch-only query (e.g., "[GlcA-a3]")
 *
 * @param {string} queryString - Query string to check
 * @returns {boolean} - True if query is branch-only
 */
export function isBranchOnlyQuery(queryString) {
  return detectSearchScopeFromQuery(queryString) === 'branches';
}

/**
 * Check if a query is a main chain-only query (e.g., "Glc-Man")
 *
 * @param {string} queryString - Query string to check
 * @returns {boolean} - True if query is main chain-only
 */
export function isMainChainOnlyQuery(queryString) {
  return detectSearchScopeFromQuery(queryString) === 'mainChain';
}

/**
 * Check if a query contains branches (e.g., "Glc-Man[GlcA]")
 *
 * @param {string} queryString - Query string to check
 * @returns {boolean} - True if query contains branches
 */
export function hasQueryBranches(queryString) {
  return /\[.*\]/.test(queryString);
}
